/**
 * InferenceDashboardPage - Privacy-First Inference Router Dashboard
 *
 * Surfaces P3 inference router backend:
 * - Provider management (enable/disable, config)
 * - Routing decision history with privacy metrics
 * - Privacy policies (read-only)
 *
 * Copyright (c) 2026 Equilateral AI. All rights reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useStatus } from '../contexts/StatusProvider';
import { Card, Badge, Table, Spinner, Select, Button, TextInput, Alert, Modal } from 'flowbite-react';
import toast from 'react-hot-toast';
import {
  getProviders,
  updateProviderConfig,
  getRoutingHistory,
  getPrivacyPolicies,
  type InferenceProvider,
  type RoutingDecision,
  type PrivacyPolicy,
} from '../api/inference';

// =============================================================================
// CONSTANTS
// =============================================================================

const PROVIDER_TYPE_COLORS: Record<string, 'success' | 'info' | 'purple'> = {
  local: 'success',
  cloud: 'info',
  hybrid: 'purple',
};

const CLASSIFICATION_COLORS: Record<string, 'gray' | 'info' | 'warning' | 'failure'> = {
  public: 'gray',
  internal: 'info',
  confidential: 'warning',
  restricted: 'failure',
};

const CLASSIFICATION_LADDER: Array<{ level: string; label: string; description: string }> = [
  { level: 'public', label: 'Public', description: 'Any provider, no scrubbing required' },
  { level: 'internal', label: 'Internal', description: 'Cloud or local providers, optional scrubbing' },
  { level: 'confidential', label: 'Confidential', description: 'Local or approved cloud, scrubbing required' },
  { level: 'restricted', label: 'Restricted', description: 'Local only, full scrubbing enforced' },
];

// =============================================================================
// COMPONENT
// =============================================================================

const InferenceDashboardPage: React.FC = () => {
  const { Client_ID } = useStatus();

  // Tab state
  const [activeTab, setActiveTab] = useState<'providers' | 'routing' | 'policies'>('providers');

  // Provider state
  const [providers, setProviders] = useState<InferenceProvider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [editingProvider, setEditingProvider] = useState<InferenceProvider | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ Latency_P50_Ms: 0, Cost_Per_1k_Tokens: 0, Enabled: true });
  const [saving, setSaving] = useState(false);

  // Routing state
  const [routingHistory, setRoutingHistory] = useState<RoutingDecision[]>([]);
  const [routingLoading, setRoutingLoading] = useState(true);
  const [routingFilter, setRoutingFilter] = useState({
    Provider_Name: '',
    Data_Classification: '',
    From_Date: '',
    To_Date: '',
  });

  // Policy state
  const [policies, setPolicies] = useState<PrivacyPolicy[]>([]);
  const [policiesLoading, setPoliciesLoading] = useState(true);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Data Fetching
  // ---------------------------------------------------------------------------

  const fetchProviders = useCallback(async () => {
    if (!Client_ID) return;
    setProvidersLoading(true);
    try {
      const res = await getProviders(Client_ID);
      if (res?.success && res?.data?.Records) {
        setProviders(res.data.Records);
      } else if (res?.success && Array.isArray(res?.data)) {
        setProviders(res.data as any);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load providers');
    } finally {
      setProvidersLoading(false);
    }
  }, [Client_ID]);

  const fetchRoutingHistory = useCallback(async () => {
    if (!Client_ID) return;
    setRoutingLoading(true);
    try {
      const opts: Record<string, string> = {};
      if (routingFilter.Provider_Name) opts.Provider_Name = routingFilter.Provider_Name;
      if (routingFilter.Data_Classification) opts.Data_Classification = routingFilter.Data_Classification;
      if (routingFilter.From_Date) opts.From_Date = routingFilter.From_Date;
      if (routingFilter.To_Date) opts.To_Date = routingFilter.To_Date;
      const res = await getRoutingHistory(Client_ID, opts);
      if (res?.success && res?.data?.Records) {
        setRoutingHistory(res.data.Records);
      } else if (res?.success && Array.isArray(res?.data)) {
        setRoutingHistory(res.data as any);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load routing history');
    } finally {
      setRoutingLoading(false);
    }
  }, [Client_ID, routingFilter]);

  const fetchPolicies = useCallback(async () => {
    if (!Client_ID) return;
    setPoliciesLoading(true);
    try {
      const res = await getPrivacyPolicies(Client_ID);
      if (res?.success && res?.data?.Records) {
        setPolicies(res.data.Records);
      } else if (res?.success && Array.isArray(res?.data)) {
        setPolicies(res.data as any);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load privacy policies');
    } finally {
      setPoliciesLoading(false);
    }
  }, [Client_ID]);

  useEffect(() => {
    if (activeTab === 'providers') fetchProviders();
    if (activeTab === 'routing') fetchRoutingHistory();
    if (activeTab === 'policies') fetchPolicies();
  }, [activeTab, fetchProviders, fetchRoutingHistory, fetchPolicies]);

  // ---------------------------------------------------------------------------
  // Provider Edit
  // ---------------------------------------------------------------------------

  const openEditModal = (provider: InferenceProvider) => {
    setEditingProvider(provider);
    setEditForm({
      Latency_P50_Ms: provider.Latency_P50_Ms,
      Cost_Per_1k_Tokens: provider.Cost_Per_1k_Tokens,
      Enabled: provider.Enabled,
    });
    setEditModalOpen(true);
  };

  const handleSaveProvider = async () => {
    if (!Client_ID || !editingProvider) return;
    setSaving(true);
    try {
      await updateProviderConfig(Client_ID, {
        Provider_ID: editingProvider.Provider_ID,
        Latency_P50_Ms: editForm.Latency_P50_Ms,
        Cost_Per_1k_Tokens: editForm.Cost_Per_1k_Tokens,
        Enabled: editForm.Enabled,
      });
      toast.success(`Provider "${editingProvider.Provider_Name}" updated`);
      setEditModalOpen(false);
      fetchProviders();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update provider');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Computed Stats
  // ---------------------------------------------------------------------------

  const providerStats = {
    total: providers.length,
    local: providers.filter(p => p.Provider_Type === 'local').length,
    cloud: providers.filter(p => p.Provider_Type === 'cloud').length,
    hybrid: providers.filter(p => p.Provider_Type === 'hybrid').length,
  };

  const routingStats = {
    total: routingHistory.length,
    scrubbingRate: routingHistory.length > 0
      ? Math.round((routingHistory.filter(r => r.Scrubbing_Applied).length / routingHistory.length) * 100)
      : 0,
    fallbackRate: routingHistory.length > 0
      ? Math.round((routingHistory.filter(r => r.Fallback_Used).length / routingHistory.length) * 100)
      : 0,
  };

  // ---------------------------------------------------------------------------
  // Render Helpers
  // ---------------------------------------------------------------------------

  if (!Client_ID) {
    return (
      <div className="p-6">
        <Alert color="warning">No client context. Please select a client to view inference data.</Alert>
      </div>
    );
  }

  const renderTabs = () => (
    <div className="border-b border-gray-200 mb-6">
      <nav className="-mb-px flex space-x-8">
        {([
          { key: 'providers', label: 'Providers' },
          { key: 'routing', label: 'Routing History' },
          { key: 'policies', label: 'Privacy Policies' },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === tab.key
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Tab 1: Providers
  // ---------------------------------------------------------------------------

  const renderProviders = () => {
    if (providersLoading) {
      return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
    }

    return (
      <>
        {/* Summary row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <p className="text-sm text-gray-500">Total Providers</p>
            <p className="text-2xl font-bold">{providerStats.total}</p>
          </Card>
          <Card>
            <p className="text-sm text-gray-500">Local</p>
            <p className="text-2xl font-bold text-green-600">{providerStats.local}</p>
          </Card>
          <Card>
            <p className="text-sm text-gray-500">Cloud</p>
            <p className="text-2xl font-bold text-blue-600">{providerStats.cloud}</p>
          </Card>
          <Card>
            <p className="text-sm text-gray-500">Hybrid</p>
            <p className="text-2xl font-bold text-purple-600">{providerStats.hybrid}</p>
          </Card>
        </div>

        {/* Provider cards grid */}
        {providers.length === 0 ? (
          <Alert color="info">No inference providers configured yet.</Alert>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {providers.map(provider => (
              <Card
                key={provider.Provider_ID}
                className={`cursor-pointer hover:shadow-lg transition-shadow ${
                  !provider.Enabled ? 'opacity-60' : ''
                }`}
                onClick={() => openEditModal(provider)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{provider.Provider_Name}</h3>
                  <Badge color={provider.Enabled ? 'success' : 'gray'}>
                    {provider.Enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>

                <div className="flex gap-2 mb-3">
                  <Badge color={PROVIDER_TYPE_COLORS[provider.Provider_Type] || 'gray'}>
                    {provider.Provider_Type}
                  </Badge>
                  <Badge color="indigo">{provider.Privacy_Level}</Badge>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Models:</span>{' '}
                    {(provider.Supported_Models || []).join(', ') || 'N/A'}
                  </div>
                  <div className="flex justify-between">
                    <span>
                      <span className="font-medium">Latency P50:</span> {provider.Latency_P50_Ms}ms
                    </span>
                    <span>
                      <span className="font-medium">Cost:</span> ${provider.Cost_Per_1k_Tokens}/1k
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Max Context:</span>{' '}
                    {(provider.Max_Context_Tokens || 0).toLocaleString()} tokens
                  </div>
                  {provider.Capabilities && provider.Capabilities.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {provider.Capabilities.map(cap => (
                        <Badge key={cap} color="gray" size="xs">{cap}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab 2: Routing History
  // ---------------------------------------------------------------------------

  const renderRoutingHistory = () => {
    if (routingLoading) {
      return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
    }

    return (
      <>
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <p className="text-sm text-gray-500">Total Routes</p>
            <p className="text-2xl font-bold">{routingStats.total}</p>
          </Card>
          <Card>
            <p className="text-sm text-gray-500">Scrubbing Rate</p>
            <p className="text-2xl font-bold">{routingStats.scrubbingRate}%</p>
          </Card>
          <Card>
            <p className="text-sm text-gray-500">Fallback Rate</p>
            <p className="text-2xl font-bold text-yellow-600">{routingStats.fallbackRate}%</p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
              <TextInput
                placeholder="Filter by provider..."
                value={routingFilter.Provider_Name}
                onChange={e => setRoutingFilter(f => ({ ...f, Provider_Name: e.target.value }))}
                sizing="sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Classification</label>
              <Select
                sizing="sm"
                value={routingFilter.Data_Classification}
                onChange={e => setRoutingFilter(f => ({ ...f, Data_Classification: e.target.value }))}
              >
                <option value="">All</option>
                <option value="public">Public</option>
                <option value="internal">Internal</option>
                <option value="confidential">Confidential</option>
                <option value="restricted">Restricted</option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <TextInput
                type="date"
                sizing="sm"
                value={routingFilter.From_Date}
                onChange={e => setRoutingFilter(f => ({ ...f, From_Date: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <TextInput
                type="date"
                sizing="sm"
                value={routingFilter.To_Date}
                onChange={e => setRoutingFilter(f => ({ ...f, To_Date: e.target.value }))}
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button size="sm" onClick={fetchRoutingHistory}>Apply Filters</Button>
          </div>
        </Card>

        {/* Table */}
        {routingHistory.length === 0 ? (
          <Alert color="info">No routing decisions found for the selected filters.</Alert>
        ) : (
          <div className="overflow-x-auto">
            <Table hoverable>
              <Table.Head>
                <Table.HeadCell>Task Type</Table.HeadCell>
                <Table.HeadCell>Selected Provider</Table.HeadCell>
                <Table.HeadCell>Classification</Table.HeadCell>
                <Table.HeadCell>Scrubbing</Table.HeadCell>
                <Table.HeadCell>Fallback</Table.HeadCell>
                <Table.HeadCell>Routed At</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {routingHistory.map(decision => (
                  <Table.Row key={decision.Decision_ID} className="bg-white">
                    <Table.Cell className="font-medium text-gray-900">
                      {decision.Task_Type}
                    </Table.Cell>
                    <Table.Cell>{decision.Selected_Provider_Name}</Table.Cell>
                    <Table.Cell>
                      <Badge color={CLASSIFICATION_COLORS[decision.Data_Classification] || 'gray'}>
                        {decision.Data_Classification}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {decision.Scrubbing_Applied ? (
                        <span className="text-green-600 font-medium">Yes</span>
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {decision.Fallback_Used ? (
                        <Badge color="warning">Fallback</Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </Table.Cell>
                    <Table.Cell className="text-sm text-gray-500">
                      {new Date(decision.Routed_At).toLocaleString()}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab 3: Privacy Policies
  // ---------------------------------------------------------------------------

  const renderPolicies = () => {
    if (policiesLoading) {
      return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
    }

    return (
      <>
        {/* Classification Ladder */}
        <Card className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Classification Ladder</h3>
          <div className="flex flex-col md:flex-row gap-0">
            {CLASSIFICATION_LADDER.map((level, idx) => (
              <div key={level.level} className="flex-1 relative">
                <div
                  className={`p-4 border rounded-lg ${
                    idx === 0 ? 'bg-gray-50 border-gray-200' :
                    idx === 1 ? 'bg-blue-50 border-blue-200' :
                    idx === 2 ? 'bg-orange-50 border-orange-200' :
                    'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Badge color={CLASSIFICATION_COLORS[level.level] || 'gray'}>{level.label}</Badge>
                    {idx < CLASSIFICATION_LADDER.length - 1 && (
                      <span className="hidden md:inline text-gray-400 absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 text-lg">
                        &rarr;
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{level.description}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Restrictions increase from left to right. Each level inherits all restrictions of the previous levels.
          </p>
        </Card>

        {/* Policy cards */}
        {policies.length === 0 ? (
          <Alert color="info">No privacy policies configured. Policies are managed by admin via the API.</Alert>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {policies.map(policy => (
              <Card key={policy.Policy_ID}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">{policy.Policy_Name}</h3>
                  <Badge color={CLASSIFICATION_COLORS[policy.Data_Classification] || 'gray'}>
                    {policy.Data_Classification}
                  </Badge>
                </div>

                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Allowed Provider Types:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(policy.Allowed_Provider_Types || []).map(type => (
                        <Badge key={type} color={PROVIDER_TYPE_COLORS[type] || 'gray'} size="xs">
                          {type}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <div>
                      <span className="font-medium text-gray-700">Require Scrubbing:</span>{' '}
                      {policy.Require_Scrubbing ? (
                        <span className="text-green-600 font-medium">Yes</span>
                      ) : (
                        <span className="text-gray-500">No</span>
                      )}
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Max Cost:</span>{' '}
                      <span>${policy.Max_Cost_Per_Request}</span>
                    </div>
                  </div>

                  {policy.Scrub_Patterns && policy.Scrub_Patterns.length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">Scrub Patterns:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {policy.Scrub_Patterns.map(pattern => (
                          <Badge key={pattern} color="pink" size="xs">{pattern}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </>
    );
  };

  // ---------------------------------------------------------------------------
  // Main Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Inference Router</h1>
        <p className="text-sm text-gray-500 mt-1">
          Privacy-first inference routing - manage providers, view routing decisions, and review privacy policies.
        </p>
      </div>

      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {renderTabs()}

      {activeTab === 'providers' && renderProviders()}
      {activeTab === 'routing' && renderRoutingHistory()}
      {activeTab === 'policies' && renderPolicies()}

      {/* Edit Provider Modal */}
      <Modal show={editModalOpen} onClose={() => setEditModalOpen(false)} size="md">
        <Modal.Header>
          Edit Provider: {editingProvider?.Provider_Name}
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Latency P50 (ms)
              </label>
              <TextInput
                type="number"
                value={editForm.Latency_P50_Ms}
                onChange={e => setEditForm(f => ({ ...f, Latency_P50_Ms: Number(e.target.value) }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cost per 1k Tokens ($)
              </label>
              <TextInput
                type="number"
                step="0.001"
                value={editForm.Cost_Per_1k_Tokens}
                onChange={e => setEditForm(f => ({ ...f, Cost_Per_1k_Tokens: Number(e.target.value) }))}
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Enabled</label>
              <button
                type="button"
                onClick={() => setEditForm(f => ({ ...f, Enabled: !f.Enabled }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  editForm.Enabled ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    editForm.Enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSaveProvider} disabled={saving}>
            {saving ? <Spinner size="sm" className="mr-2" /> : null}
            Save Changes
          </Button>
          <Button color="gray" onClick={() => setEditModalOpen(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default InferenceDashboardPage;
